// Scale-aware programmatic scrolling for the form view (task 1.3). The widget
// renders inside a transform:scale wrapper in production, where native
// `scrollIntoView({ behavior: 'smooth' })` misfires: getBoundingClientRect
// returns *visual* (post-transform) pixels while scrollTop/clientHeight are
// layout pixels, so the browser lands the scroll in the wrong place. We divide
// the scale factor out and drive scrollTop ourselves with rAF — a port of the
// prototype's efSmoothScrollTo/efScrollEl (app.jsx).

/** Anchor: the element's top lands this far below the scroller's top, keeping
 *  a tall field plus its help text readable (prototype metric). */
export const SCROLL_ANCHOR_OFFSET = 56;

/** Animation length of the eased scroll. */
export const SCROLL_DURATION_MS = 720;

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Clamp a target scrollTop to the scroller's real range. */
const clampTop = (scroller: HTMLElement, top: number): number =>
    Math.max(0, Math.min(top, scroller.scrollHeight - scroller.clientHeight));

/**
 * Animate `scroller.scrollTop` to `top` (clamped) over `duration` with an
 * ease-in-out curve. Under `prefers-reduced-motion: reduce` the jump is
 * instant. A trailing timeout guarantees the final position even when rAF is
 * throttled (e.g. a backgrounded iframe).
 */
export const smoothScrollTo = (
    scroller: HTMLElement,
    top: number,
    duration: number = SCROLL_DURATION_MS
): void => {
    const target = clampTop(scroller, top);
    if (prefersReducedMotion()) {
        scroller.scrollTop = target;
        return;
    }
    const start = scroller.scrollTop;
    const diff = target - start;
    if (Math.abs(diff) < 2) return;
    const t0 = performance.now();
    const step = (now: number) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        scroller.scrollTop = start + diff * eased;
        if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    window.setTimeout(() => {
        if (Math.abs(scroller.scrollTop - target) > 3) scroller.scrollTop = target;
    }, duration + 80);
};

export type ScrollAlign = 'start' | 'center';

/**
 * The layout-space scrollTop that places `el` at the anchor within `scroller`.
 * `start` anchors the element's top SCROLL_ANCHOR_OFFSET below the scroller's
 * top; `center` centres it in the viewport (never higher than the anchor —
 * an element taller than the viewport degrades to the start anchor).
 *
 * Scale awareness: both rects are visual pixels while clientHeight is layout
 * pixels, so their ratio is the widget's scale factor — visual distances are
 * divided by it before entering scrollTop space.
 */
export const scrollTargetFor = (
    scroller: HTMLElement,
    el: HTMLElement,
    align: ScrollAlign = 'start'
): number => {
    const sr = scroller.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const scale = sr.height / scroller.clientHeight || 1;
    const offset =
        align === 'center'
            ? Math.max((scroller.clientHeight - er.height / scale) / 2, SCROLL_ANCHOR_OFFSET)
            : SCROLL_ANCHOR_OFFSET;
    return clampTop(scroller, scroller.scrollTop + (er.top - sr.top) / scale - offset);
};

/** Smooth-scroll `el` to its anchored position within `scroller`. */
export const scrollToElement = (
    scroller: HTMLElement | null | undefined,
    el: HTMLElement | null | undefined,
    align: ScrollAlign = 'start'
): void => {
    if (!scroller || !el) return;
    smoothScrollTo(scroller, scrollTargetFor(scroller, el, align));
};
